// src/lib/black-friday-product-comparison.ts
import { BFProductData, BFProductComparison } from "@/types/black-friday";

/**
 * Create product comparison data by matching products across years
 * @param products25 - 2025 product data
 * @param productsComp - Comparison year product data (2024 or 2023)
 * @param matchOn - Field to match products on ('productId' or 'productTitle')
 * @returns Array of product comparisons
 */
export function createProductComparison(
  products25: BFProductData[],
  productsComp: BFProductData[],
  matchOn: 'productId' | 'productTitle' = 'productId'
): BFProductComparison[] {
  // Create lookup map for comparison year products
  const comparisonMap = new Map<string, BFProductData>();
  productsComp.forEach(product => {
    const key = matchOn === 'productId' ? product.productId : product.productTitle;
    if (key && key !== 'N/A' && key.trim() !== '') {
      comparisonMap.set(key, product);
    }
  });

  // Create comparison array
  const comparisons: BFProductComparison[] = [];

  // Process 2025 products
  products25.forEach(product25 => {
    const matchKey = matchOn === 'productId' ? product25.productId : product25.productTitle;
    const compProduct = comparisonMap.get(matchKey);

    const comparison: BFProductComparison = {
      productId: product25.productId,
      productTitle: product25.productTitle,

      // 2025 data
      impressions25: product25.impressions,
      cost25: product25.cost,
      conv25: product25.conv,
      convValue25: product25.convValue,

      // Comparison data
      impressionsComp: compProduct?.impressions || 0,
      costComp: compProduct?.cost || 0,
      convComp: compProduct?.conv || 0,
      convValueComp: compProduct?.convValue || 0,

      // Calculate changes
      impressionsChange: product25.impressions - (compProduct?.impressions || 0),
      impressionsChangePct: calculatePercentageChange(product25.impressions, compProduct?.impressions || 0),
      costChange: product25.cost - (compProduct?.cost || 0),
      costChangePct: calculatePercentageChange(product25.cost, compProduct?.cost || 0),
      convChange: product25.conv - (compProduct?.conv || 0),
      convChangePct: calculatePercentageChange(product25.conv, compProduct?.conv || 0),
      convValueChange: product25.convValue - (compProduct?.convValue || 0),
      convValueChangePct: calculatePercentageChange(product25.convValue, compProduct?.convValue || 0),

      // Flags
      hasCurrentData: true,
      hasComparisonData: !!compProduct,
    };

    comparisons.push(comparison);

    // Remove from comparison map to track processed products
    if (compProduct) {
      comparisonMap.delete(matchKey);
    }
  });

  // Add products that exist in comparison year but not in 2025
  comparisonMap.forEach(compProduct => {
    const comparison: BFProductComparison = {
      productId: compProduct.productId,
      productTitle: compProduct.productTitle,

      // No 2025 data
      impressions25: 0,
      cost25: 0,
      conv25: 0,
      convValue25: 0,

      // Comparison data
      impressionsComp: compProduct.impressions,
      costComp: compProduct.cost,
      convComp: compProduct.conv,
      convValueComp: compProduct.convValue,

      // Calculate changes (all negative since no 2025 data)
      impressionsChange: -compProduct.impressions,
      impressionsChangePct: -100,
      costChange: -compProduct.cost,
      costChangePct: -100,
      convChange: -compProduct.conv,
      convChangePct: -100,
      convValueChange: -compProduct.convValue,
      convValueChangePct: -100,

      // Flags
      hasCurrentData: false,
      hasComparisonData: true,
    };

    comparisons.push(comparison);
  });

  return comparisons;
}

/**
 * Calculate percentage change between two values
 */
function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
}

/**
 * Filter product comparisons based on data availability
 */
export function filterProductComparisons(
  comparisons: BFProductComparison[],
  filterType: 'all' | 'new' | 'existing' | 'missing'
): BFProductComparison[] {
  switch (filterType) {
    case 'new':
      // Products that exist in 2025 but not in comparison year
      return comparisons.filter(c => c.hasCurrentData && !c.hasComparisonData);
    case 'existing':
      // Products that exist in both years
      return comparisons.filter(c => c.hasCurrentData && c.hasComparisonData);
    case 'missing':
      // Products that existed in comparison year but not in 2025
      return comparisons.filter(c => !c.hasCurrentData && c.hasComparisonData);
    case 'all':
    default:
      return comparisons;
  }
}

/**
 * Sort product comparisons
 */
export function sortProductComparisons(
  comparisons: BFProductComparison[],
  sortColumn: string,
  direction: 'asc' | 'desc'
): BFProductComparison[] {
  const sorted = [...comparisons].sort((a, b) => {
    const aVal = a[sortColumn as keyof BFProductComparison];
    const bVal = b[sortColumn as keyof BFProductComparison];

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    const aNum = Number(aVal) || 0;
    const bNum = Number(bVal) || 0;

    return direction === 'asc' ? aNum - bNum : bNum - aNum;
  });

  return sorted;
}

/**
 * Determine which field to use for matching (productId or productTitle)
 * Returns 'productId' if most products have valid IDs, otherwise 'productTitle'
 */
export function determineMatchingMethod(products: BFProductData[]): 'productId' | 'productTitle' {
  if (products.length === 0) return 'productId';

  const validIds = products.filter(p => p.productId && p.productId !== 'N/A' && p.productId.trim() !== '').length;
  const threshold = products.length * 0.5; // 50% threshold

  return validIds >= threshold ? 'productId' : 'productTitle';
}
